import { DeviceSubCategory } from '@eppendorf/app-configs';
import {
  Badge,
  ButtonBase,
  Checkbox,
  Icon,
  IconSizeClasses,
  Tooltip,
} from '@eppendorf/vnls-react-components';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import { DeviceCategoryConfig } from '$components/app-config/app-config.types';
import { getCategoryIconName } from '$features/notifications/notification-category.utils';

export type NotificationCategoryCardProps = {
  category: DeviceCategoryConfig;
  selectedSubCategories: DeviceSubCategory[];
  onToggle: (subCategory: DeviceSubCategory) => void;
};

export function CategoryCardHeader({ category }: { category: DeviceCategoryConfig }) {
  const { t } = useTranslation();

  return (
    <div className="condition-category-card__header">
      <Icon
        name={getCategoryIconName(category.key)}
        size={IconSizeClasses.Small}
        className="condition-category-card__icon"
      />
      <div className="condition-category-card__heading">
        <div className="flex flex__ai--center gap-s">
          <h3 className="font-size-m font-weight-semibold">{category.name}</h3>
          <Tooltip
            trigger={
              <ButtonBase
                className="condition-category-card__info"
                title={t('tagsCondition.advanced.aboutCategory', {
                  category: category.name,
                })}
              >
                <Icon name="info" size={IconSizeClasses.XSmall} />
              </ButtonBase>
            }
          >
            <span className="condition-category-card__tooltip">
              {category.description}
            </span>
          </Tooltip>
        </div>
        {category.appliesToAllModels === false &&
          Boolean(category.applicableModels?.length) && (
            <p className="color-gray-800 font-size-s">
              {t('tagsCondition.advanced.appliesTo', {
                models: category.applicableModels?.join(', '),
              })}
            </p>
          )}
      </div>
    </div>
  );
}

export function NotificationCategoryCard({
  category,
  selectedSubCategories,
  onToggle,
}: NotificationCategoryCardProps) {
  const { t } = useTranslation();
  const cardId = useId();
  const hasSelection = selectedSubCategories.length > 0;

  return (
    <section
      className={`condition-category-card${hasSelection ? ' condition-category-card--selected' : ''}`}
    >
      <CategoryCardHeader category={category} />
      <div className="condition-category-card__options">
        {category.subCategories.map((subCategory) => {
          const id = `${cardId}-${subCategory.key}`;
          return (
            <div key={subCategory.key} className="condition-category-option">
              <Checkbox
                id={id}
                checked={selectedSubCategories.includes(subCategory.key)}
                onChange={() => onToggle(subCategory.key)}
                ariaLabel={`${category.name}: ${subCategory.name}`}
              />
              <label htmlFor={id} className="condition-category-option__label">
                <span>{subCategory.name}</span>
                {subCategory.requiresImmediateAttention && (
                  <Tooltip
                    trigger={
                      <ButtonBase className="condition-category-card__info">
                        <Badge color="red" size="small">
                          {t('tagsCondition.advanced.recommended')}
                        </Badge>
                      </ButtonBase>
                    }
                  >
                    <span className="condition-category-card__tooltip">
                      {t('tagsCondition.advanced.recommendedTooltip')}
                    </span>
                  </Tooltip>
                )}
              </label>
            </div>
          );
        })}
      </div>
    </section>
  );
}
